'use client'

import { Khatm } from '@/services/khatm/khatm.type'
import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import Typography from '@/components/ui/Typography'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useToggle } from 'react-use'
import { useAddToKhatm } from '@/services/khatm/khatm.hook'
import { isSuccessReq } from '@/utils/common'
import { useRouter } from 'next/navigation'
import BackButton from '@/components/BackButton'
import CopyModal from '@/components/CopyModal'

type FormType = {
  count: string
}

function KhatmById({ created_at, id, total_count, title, khatm_type }: Khatm) {
  const router = useRouter()
  const { mutate: addToKhatm, isPending } = useAddToKhatm(id)
  const { formState, control, handleSubmit, getValues, setValue } =
    useForm<FormType>({
      defaultValues: { count: '' },
    })
  const [confirmModal, confirmModalToggle] = useToggle(false)
  const [shareModal, shareModaltoggle] = useToggle(false)

  const [selectedCount, setSelectedCount] = React.useState('')

  const [showCustomInput, setShowCustomInput] = React.useState(false)

  const openConfirmModal = () => {
    confirmModalToggle(true)
  }

  const handleAfterSuccess = () => {
    openConfirmModal()
    router.refresh()
  }

  const handleAddToKhatm = (data: FormType) => {
    setSelectedCount(data.count)
    addToKhatm(
      { count: data.count },
      {
        onSuccess(data) {
          if (isSuccessReq(data.status)) {
            handleAfterSuccess()
          }
        },
      }
    )
  }

  const handleQuickAdd = (count: number) => {
    const value = String(count)
    setSelectedCount(value)

    addToKhatm(
      { count: value },
      {
        onSuccess(data) {
          if (isSuccessReq(data.status)) {
            handleAfterSuccess()
          }
        },
      }
    )
  }

  return (
    <>
      <BackButton />
      {/* info kaht */}
      <div className='rounded-[20px] border-[0.5px] px-4 py-6'>
        <Typography className='mb-2 text-secondary' weight='light' center>
          ختم {khatm_type}
        </Typography>
        <Typography
          className='break-words text-secondary'
          center
          weight='semibold'
          variant='h6'
        >
          {title}
        </Typography>

        <Typography className='mt-5 text-secondary' weight='light' center>
          شروع ختم
        </Typography>
        <Typography
          className='mt-4 text-secondary-3'
          variant='small'
          weight='light'
          center
        >
          {new Date(created_at).toLocaleDateString('fa')}
        </Typography>
        <Typography className='mt-5 text-secondary' center>
          تعداد ختم‌شده تاکنون
        </Typography>

        <Typography
          variant='h4'
          className='text-primary-gr mt-4'
          weight='bold'
          center
        >
          {total_count}
        </Typography>
      </div>

      {/* add to khatm */}
      <div className='bg-primary-cart my-3 rounded-[20px] p-4'>
        <Typography
          className='text-onPrimary'
          variant='h6'
          weight='semibold'
          center
        >
          من هم سهیم میشوم
        </Typography>

        <div className='mt-6 space-y-3'>
          <Button
            variant='secondary'
            className='h-12 w-full text-center'
            onClick={() => handleQuickAdd(1)}
            isLoading={isPending}
          >
            یک عدد
          </Button>

          <div className='grid grid-cols-4 gap-2'>
            <Button
              variant='secondary'
              className='h-10'
              onClick={() => handleQuickAdd(5)}
              isLoading={isPending}
            >
              ۵
            </Button>
            <Button
              variant='secondary'
              className='h-10'
              onClick={() => handleQuickAdd(10)}
              isLoading={isPending}
            >
              ۱۰
            </Button>
            <Button
              variant='secondary'
              className='h-10'
              onClick={() => handleQuickAdd(50)}
              isLoading={isPending}
            >
              ۵۰
            </Button>
            <Button
              variant='secondary'
              className='h-10'
              onClick={() => setShowCustomInput(prev => !prev)}
            >
              دلخواه
            </Button>
          </div>
        </div>

        {showCustomInput && (
          <form
            onSubmit={handleSubmit(handleAddToKhatm)}
            className='mt-6 flex w-full items-center justify-between gap-3'
          >
            <Input
              placeholder='تعداد را وارد کنید'
              control={control}
              formState={formState}
              name='count'
              type='number'
              noError
              inputMode='numeric'
              validation={{
                required: 'تعداد الزامی است',
                max: {
                  value: 1000,
                  message: 'حداکثر تعداد 1000 است',
                },
              }}
              className='shadow-1 text-secondary-content h-12 w-full rounded-xl bg-onSecondary-1 p-2 text-xs placeholder:text-xs placeholder:text-onSecondary-4'
            />
            <Button
              isIconOnly
              className='m-0 flex aspect-square h-12 w-12 cursor-pointer items-center justify-center bg-onPrimary p-0 text-primary'
              type='submit'
              isLoading={isPending}
            >
              <i className='icon-send text-2xl' />
            </Button>
          </form>
        )}

        <div className='mt-4 flex gap-2'>
          <span className='text-[#FF4949]'>*</span>
          <Typography variant='small' className='text-onPrimary'>
            در صورت عدم التزام به تعداد وارد شده، مسئولیت شرعی آن به عهده شما
            خواهد بود.
          </Typography>
        </div>
      </div>
      <section className='mt-auto'>
        <Button
          fullWidth
          size='lg'
          icon={<i className='icon-share text-2xl' />}
          onClick={shareModaltoggle}
        >
          اشتراک‌گذاری ختم
        </Button>
      </section>

      {/* modal */}
      <Modal
        className='space-y-4 pt-2'
        isOpen={confirmModal}
        setIsOpen={confirmModalToggle}
        title={
          <Typography className='font-nastaliq text-[32px] text-primary' center>
            سعیکم مشکوراً
          </Typography>
        }
        description={
          <Typography className='text-secondary' weight='semibold' variant='h6'>
            شما {selectedCount} {khatm_type} هدیه کردید.
          </Typography>
        }
        confirmText='تایید'
        onConfirm={() => {
          confirmModalToggle(false)
          setValue('count', '')
          setSelectedCount('')
        }}
      />
      <CopyModal
        shareModal={shareModal}
        shareModaltoggle={shareModaltoggle}
        link={
          typeof window !== 'undefined'
            ? `${window.location.origin}/azkar/list/${id}`
            : ''
        }
      />
    </>
  )
}

export default KhatmById
